import React, { useId, useState } from "react";
import { CONTACT, FORM_MESSAGES } from "../constants/config";
import { sendCareerApplication, sendContactEmail } from "../services/contactEmail";
import { Reveal, StaggerGroup, StaggerItem } from "./Reveal";

const inputClassName =
  "w-full rounded-lg border border-indigo-200 bg-white px-4 py-3 text-sm text-slate-700 placeholder:text-slate-400 transition-colors focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 sm:text-base";

const ATTACHMENT_MAX_BYTES = 8 * 1024 * 1024; // 8 MB
const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.png,.jpg,.jpeg";
const ATTACHMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/png",
  "image/jpeg",
];

const PHONE_RE = /^[+()0-9\s-]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateAttachment(file, { required }) {
  if (!file) {
    return required ? "Please attach your resume" : "";
  }
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return "Attachment must be under 8 MB";
  }
  if (ATTACHMENT_TYPES.length && file.type && !ATTACHMENT_TYPES.includes(file.type)) {
    return "Upload a PDF, Word document, or image";
  }
  return "";
}

function LocationCard({ title, lines, index }) {
  return (
    <StaggerItem index={index}>
      <div className="h-full rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 text-center sm:p-5 sm:text-left">
        <h3 className="text-base font-bold text-indigo-900 sm:text-lg">
          {title}
        </h3>
        {lines.map((line) => (
          <p key={line} className="mt-1 text-sm text-gray-600 sm:text-base">
            {line}
          </p>
        ))}
      </div>
    </StaggerItem>
  );
}

function ContactInfo() {
  return (
    <Reveal variant="slideRight" className="flex flex-col gap-6 md:gap-8">
      <div className="flex flex-col items-center gap-2 text-center xl:items-start xl:text-left">
        <div className="text-[1.15rem] font-semibold uppercase tracking-[0.2em] text-indigo-600 xl:text-[1.35rem]">
          {CONTACT.eyebrow}
        </div>
        <h2 className="text-[1.575rem] font-extrabold leading-[1.25] tracking-tight text-indigo-950 sm:text-[1.8rem] md:text-[2.25rem] xl:text-[2.475rem] 2xl:text-[2.8125rem]">
          {CONTACT.title}
        </h2>
        <p className="mx-auto max-w-xl text-[0.72rem] leading-relaxed text-gray-500 sm:text-[0.81rem] xl:mx-0 xl:text-[1.0125rem]">
          {CONTACT.subtitle}
        </p>
      </div>

      <StaggerGroup className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {CONTACT.locations.map((location, index) => (
          <LocationCard
            key={location.title}
            title={location.title}
            lines={location.lines}
            index={index}
          />
        ))}
      </StaggerGroup>
    </Reveal>
  );
}

function RequiredMark() {
  return (
    <span className="text-red-300" aria-hidden="true">
      {" "}
      *
    </span>
  );
}

function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <span id={id} className="text-sm font-medium text-red-300" role="alert">
      {children}
    </span>
  );
}

function Honeypot({ value, onChange, idPrefix }) {
  return (
    <div
      className="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
      aria-hidden="true"
    >
      <label htmlFor={`${idPrefix}-website`}>Website</label>
      <input
        id={`${idPrefix}-website`}
        type="text"
        name="website"
        value={value}
        onChange={onChange}
        tabIndex={-1}
        autoComplete="off"
      />
    </div>
  );
}

function SubmitStatus({ submitted, submitError }) {
  return (
    <>
      {submitted && (
        <div
          className="rounded-lg bg-emerald-500 p-3 text-center text-sm font-semibold text-white sm:text-base"
          role="status"
          aria-live="polite"
        >
          Thank you for contacting TowerEdge. We will get back to you soon.
        </div>
      )}

      {submitError && (
        <div
          className="rounded-lg bg-red-500/90 p-3 text-center text-sm font-semibold text-white sm:text-base"
          role="alert"
        >
          {submitError}
        </div>
      )}
    </>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      className={`min-h-[44px] flex-1 rounded-full px-4 text-sm font-semibold transition-colors sm:text-base ${
        active
          ? "bg-white text-indigo-900 shadow-md"
          : "text-indigo-200 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function AttachmentField({
  uid,
  file,
  onChange,
  error,
  loading,
  label,
  hint,
  ariaLabel,
  required,
  resetKey,
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={`${uid}-attachment`}
        className="text-sm font-semibold text-white"
      >
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        key={resetKey}
        id={`${uid}-attachment`}
        type="file"
        name="attachment"
        accept={ATTACHMENT_ACCEPT}
        onChange={onChange}
        disabled={loading}
        aria-label={ariaLabel}
        aria-required={required ? "true" : "false"}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={
          error ? `${uid}-attachment-error` : `${uid}-attachment-hint`
        }
        className={`${inputClassName} cursor-pointer file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-indigo-100 file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-indigo-900 hover:file:bg-indigo-200`}
      />
      {file && !error && (
        <span className="text-xs text-indigo-200">Selected: {file.name}</span>
      )}
      {!error && !file && (
        <span id={`${uid}-attachment-hint`} className="text-xs text-indigo-200">
          {hint}
        </span>
      )}
      <FieldError id={`${uid}-attachment-error`}>{error}</FieldError>
    </div>
  );
}

function ProjectForm() {
  const uid = useId();
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
    website: "", // honeypot — leave empty
  });
  const [attachment, setAttachment] = useState(null);
  const [attachmentResetKey, setAttachmentResetKey] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (submitError) setSubmitError("");
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0] || null;
    setAttachment(file);
    if (errors.attachment) {
      setErrors((prev) => ({ ...prev, attachment: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_RE.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!PHONE_RE.test(formData.phone.trim())) {
      newErrors.phone = "Enter a valid phone number";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";

    const attachmentError = validateAttachment(attachment, { required: false });
    if (attachmentError) newErrors.attachment = attachmentError;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      await sendContactEmail({ ...formData, file: attachment });
      setSubmitted(true);
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        message: "",
        website: "",
      });
      setAttachment(null);
      setAttachmentResetKey((key) => key + 1);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      setSubmitError(
        error?.message ||
          "Failed to send message. Please try again or email us directly.",
      );
      if (error?.fieldErrors) setErrors(error.fieldErrors);
      console.error("Project enquiry form error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col gap-4 sm:gap-5"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-name`}
            className="text-sm font-semibold text-white"
          >
            Name
            <RequiredMark />
          </label>
          <input
            id={`${uid}-name`}
            type="text"
            name="name"
            placeholder={FORM_MESSAGES.namePlaceholder}
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.nameLabelAriaLabel}
            aria-required="true"
            aria-invalid={errors.name ? "true" : "false"}
            aria-describedby={errors.name ? `${uid}-name-error` : undefined}
            className={inputClassName}
          />
          <FieldError id={`${uid}-name-error`}>{errors.name}</FieldError>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-company`}
            className="text-sm font-semibold text-white"
          >
            Company
          </label>
          <input
            id={`${uid}-company`}
            type="text"
            name="company"
            placeholder={FORM_MESSAGES.companyPlaceholder}
            value={formData.company}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.companyLabelAriaLabel}
            className={inputClassName}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-email`}
            className="text-sm font-semibold text-white"
          >
            Email
            <RequiredMark />
          </label>
          <input
            id={`${uid}-email`}
            type="email"
            name="email"
            placeholder={FORM_MESSAGES.emailPlaceholder}
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.emailLabelAriaLabel}
            aria-required="true"
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={errors.email ? `${uid}-email-error` : undefined}
            className={inputClassName}
          />
          <FieldError id={`${uid}-email-error`}>{errors.email}</FieldError>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-phone`}
            className="text-sm font-semibold text-white"
          >
            Phone
            <RequiredMark />
          </label>
          <input
            id={`${uid}-phone`}
            type="tel"
            name="phone"
            placeholder={FORM_MESSAGES.phonePlaceholder}
            value={formData.phone}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.phoneLabelAriaLabel}
            aria-required="true"
            aria-invalid={errors.phone ? "true" : "false"}
            aria-describedby={errors.phone ? `${uid}-phone-error` : undefined}
            className={inputClassName}
          />
          <FieldError id={`${uid}-phone-error`}>{errors.phone}</FieldError>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${uid}-message`}
          className="text-sm font-semibold text-white"
        >
          Message
          <RequiredMark />
        </label>
        <textarea
          id={`${uid}-message`}
          name="message"
          placeholder={FORM_MESSAGES.messagePlaceholder}
          value={formData.message}
          onChange={handleChange}
          disabled={loading}
          aria-label={FORM_MESSAGES.messageLabelAriaLabel}
          aria-required="true"
          aria-invalid={errors.message ? "true" : "false"}
          aria-describedby={errors.message ? `${uid}-message-error` : undefined}
          rows={5}
          className={`${inputClassName} min-h-[120px] resize-y`}
        />
        <FieldError id={`${uid}-message-error`}>{errors.message}</FieldError>
      </div>

      <AttachmentField
        uid={uid}
        file={attachment}
        onChange={handleAttachmentChange}
        error={errors.attachment}
        loading={loading}
        label={FORM_MESSAGES.attachmentLabel}
        hint={FORM_MESSAGES.attachmentHint}
        ariaLabel={FORM_MESSAGES.attachmentLabelAriaLabel}
        required={false}
        resetKey={attachmentResetKey}
      />

      <Honeypot
        value={formData.website}
        onChange={handleChange}
        idPrefix={uid}
      />

      <button
        type="submit"
        disabled={loading}
        className="min-h-[48px] w-full rounded-lg bg-white py-3 text-sm font-bold text-indigo-900 transition-colors duration-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-900 disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
        aria-label={FORM_MESSAGES.submitButton}
      >
        {loading ? "Sending…" : FORM_MESSAGES.submitButton}
      </button>

      <SubmitStatus submitted={submitted} submitError={submitError} />
    </form>
  );
}

function CareerForm() {
  const uid = useId();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "", // honeypot — leave empty
  });
  const [resume, setResume] = useState(null);
  const [resumeResetKey, setResumeResetKey] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (submitError) setSubmitError("");
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0] || null;
    setResume(file);

    if (errors.resume) {
      setErrors((prev) => ({ ...prev, resume: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_RE.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!PHONE_RE.test(formData.phone.trim())) {
      newErrors.phone = "Enter a valid phone number";
    }
    if (!formData.message.trim()) newErrors.message = "Message is required";

    const resumeError = validateAttachment(resume, { required: true });
    if (resumeError) newErrors.resume = resumeError;

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setSubmitError("");

    try {
      await sendCareerApplication({ ...formData, resume });
      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "", website: "" });
      setResume(null);
      setResumeResetKey((key) => key + 1);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      setSubmitError(
        error?.message ||
          "Failed to submit your application. Please try again or email us directly.",
      );
      if (error?.fieldErrors) {
        // Backend reports the resume field as "attachment" — map it back.
        const { attachment, ...rest } = error.fieldErrors;
        setErrors({ ...rest, ...(attachment ? { resume: attachment } : {}) });
      }
      console.error("Career application form error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col gap-4 sm:gap-5"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-name`}
            className="text-sm font-semibold text-white"
          >
            Name
            <RequiredMark />
          </label>
          <input
            id={`${uid}-name`}
            type="text"
            name="name"
            placeholder={FORM_MESSAGES.namePlaceholder}
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.nameLabelAriaLabel}
            aria-required="true"
            aria-invalid={errors.name ? "true" : "false"}
            aria-describedby={errors.name ? `${uid}-name-error` : undefined}
            className={inputClassName}
          />
          <FieldError id={`${uid}-name-error`}>{errors.name}</FieldError>
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor={`${uid}-phone`}
            className="text-sm font-semibold text-white"
          >
            Phone
            <RequiredMark />
          </label>
          <input
            id={`${uid}-phone`}
            type="tel"
            name="phone"
            placeholder={FORM_MESSAGES.phonePlaceholder}
            value={formData.phone}
            onChange={handleChange}
            disabled={loading}
            aria-label={FORM_MESSAGES.phoneLabelAriaLabel}
            aria-required="true"
            aria-invalid={errors.phone ? "true" : "false"}
            aria-describedby={errors.phone ? `${uid}-phone-error` : undefined}
            className={inputClassName}
          />
          <FieldError id={`${uid}-phone-error`}>{errors.phone}</FieldError>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${uid}-email`}
          className="text-sm font-semibold text-white"
        >
          Email
          <RequiredMark />
        </label>
        <input
          id={`${uid}-email`}
          type="email"
          name="email"
          placeholder={FORM_MESSAGES.careerEmailPlaceholder}
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
          aria-label={FORM_MESSAGES.emailLabelAriaLabel}
          aria-required="true"
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby={errors.email ? `${uid}-email-error` : undefined}
          className={inputClassName}
        />
        <FieldError id={`${uid}-email-error`}>{errors.email}</FieldError>
      </div>

      <AttachmentField
        uid={uid}
        file={resume}
        onChange={handleResumeChange}
        error={errors.resume}
        loading={loading}
        label={FORM_MESSAGES.resumeLabel}
        hint={FORM_MESSAGES.resumeHint}
        ariaLabel={FORM_MESSAGES.resumeLabelAriaLabel}
        required
        resetKey={resumeResetKey}
      />

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`${uid}-message`}
          className="text-sm font-semibold text-white"
        >
          Message
          <RequiredMark />
        </label>
        <textarea
          id={`${uid}-message`}
          name="message"
          placeholder={FORM_MESSAGES.careerMessagePlaceholder}
          value={formData.message}
          onChange={handleChange}
          disabled={loading}
          aria-label={FORM_MESSAGES.messageLabelAriaLabel}
          aria-required="true"
          aria-invalid={errors.message ? "true" : "false"}
          aria-describedby={errors.message ? `${uid}-message-error` : undefined}
          rows={5}
          className={`${inputClassName} min-h-[120px] resize-y`}
        />
        <FieldError id={`${uid}-message-error`}>{errors.message}</FieldError>
      </div>

      <Honeypot
        value={formData.website}
        onChange={handleChange}
        idPrefix={uid}
      />

      <button
        type="submit"
        disabled={loading}
        className="min-h-[48px] w-full rounded-lg bg-white py-3 text-sm font-bold text-indigo-900 transition-colors duration-300 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-900 disabled:cursor-not-allowed disabled:opacity-70 sm:text-base"
        aria-label={FORM_MESSAGES.submitButtonCareer}
      >
        {loading ? "Submitting…" : FORM_MESSAGES.submitButtonCareer}
      </button>

      <SubmitStatus submitted={submitted} submitError={submitError} />
    </form>
  );
}

const TAB_COPY = {
  project: {
    heading: "Send us a message",
    subheading: "Fill in your details and we’ll respond shortly.",
  },
  career: {
    heading: "Apply to join our team",
    subheading: "Share your details and resume — we’ll be in touch.",
  },
};

function ContactFormCard() {
  const [tab, setTab] = useState("career");
  const copy = TAB_COPY[tab];

  return (
    <Reveal variant="slideLeft" index={2}>
      <div className="relative flex w-full flex-col gap-4 rounded-2xl border border-indigo-100 bg-indigo-900 p-5 shadow-xl sm:gap-5 sm:p-6 md:p-8">
        <div
          role="tablist"
          aria-label="Contact form type"
          className="flex gap-1 rounded-full bg-indigo-950/50 p-1"
        >
          <TabButton active={tab === "career"} onClick={() => setTab("career")}>
            {FORM_MESSAGES.tabs.career}
          </TabButton>
          <TabButton
            active={tab === "project"}
            onClick={() => setTab("project")}
          >
            {FORM_MESSAGES.tabs.project}
          </TabButton>
        </div>

        <div className="border-b border-indigo-700 pb-4">
          <h3 className="text-xl font-bold text-white sm:text-2xl">
            {copy.heading}
          </h3>
          <p className="mt-1 text-sm text-indigo-200 sm:text-base">
            {copy.subheading}
          </p>
        </div>

        {tab === "project" ? <ProjectForm /> : <CareerForm />}
      </div>
    </Reveal>
  );
}

export function ContactSection() {
  return (
    <section
      className="w-full bg-stone-50 px-4 py-12 sm:px-6 md:py-20 lg:px-10 xl:px-14 2xl:px-20"
      aria-label="Contact section"
    >
      <div className="mx-auto grid w-full max-w-[1800px] grid-cols-1 items-start gap-8 xl:grid-cols-2 xl:gap-12 2xl:gap-16">
        <ContactInfo />
        <ContactFormCard />
      </div>
    </section>
  );
}
